<template>
    <div class="page-container" :class="{ collapsed: !$store.getters.sidebarOpened }">
        <div class="area-container left-area">
            <div class="area-tools">
                <el-button
                    type="primary"
                    :icon="Upload"
                    @click="exportData"
                    v-permission="['pipeline-sensor:history-data:batch-export']"
                >
                    最新数据导出
                </el-button>
            </div>
            <el-scrollbar>
                <el-tree
                    ref="area_tree"
                    node-key="id"
                    :data="treeData"
                    :props="{
                        label: 'name',
                        children: 'children',
                        isLeaf: 'is_leaf'
                    }"
                    :expand-on-click-node="false"
                    :default-expanded-keys="default_expanded_keys"
                    :load="loadNode"
                    highlight-current
                    lazy
                    @node-click="handleNodeClick"
                >
                    <template #default="{ node, data }">
                        <span v-if="node.label">{{ node.label }}</span>
                        <span v-else>IMEI:{{ data.IMEI }}</span>
                    </template>
                </el-tree>
            </el-scrollbar>
        </div>
        <div class="data-container center-area">
            <div class="tool-box">
                <el-button type="primary" :icon="Refresh" @click="resetQueryForm">刷新</el-button>
            </div>
            <el-form class="search-form" :inline="true" :model="search_data" label-position="top">
                <el-form-item label="采集时间">
                    <el-date-picker
                        v-model="search_data.date_range"
                        type="datetimerange"
                        range-separator="至"
                        start-placeholder="开始日期"
                        end-placeholder="结束日期"
                        value-format="YYYY-MM-DD HH:mm:ss"
                        style="width: 100%"
                    />
                </el-form-item>
                <el-form-item>
                    <el-button type="primary" :icon="Search" plain @click="getTableData">搜索</el-button>
                    <el-button :icon="Refresh" @click="resetQueryForm">重置</el-button>
                </el-form-item>
            </el-form>
            <el-table
                class="data-table"
                :data="table_data"
                border
                style="width: 100%"
                v-loading="tableLoading"
                row-key="id"
                :empty-text="current_node?.level === 6 ? '暂无数据' : '请在左侧选择设备'"
                @selection-change="handleSelectionChange"
            >
                <el-table-column type="selection" width="55" align="center"></el-table-column>
                <el-table-column label="序号" type="index" fixed="left" width="60px" align="center"></el-table-column>
                <el-table-column label="名称" fixed="left" prop="name" width="130px" show-overflow-tooltip></el-table-column>
                <el-table-column label="设备类型 / 型号" width="150px" prop="device_type">
                    <template #default="{ row }">
                        <span v-if="row.model === null"></span>
                        <span v-else-if="model_list[row.model]">
                            {{ row.model ? model_list[row.model].type + '/' + model_list[row.model].model : '' }}
                        </span>
                        <span v-else>设备型号不存在</span>
                    </template>
                </el-table-column>
                <el-table-column label="IMEI" width="150px" prop="IMEI"></el-table-column>
                <el-table-column width="100px" prop="temperature">
                    <template #header>
                        <p>入水温度</p>
                        <p>(℃)</p>
                    </template>
                    <template #default="{ row }">
                        <span v-if="row.roottemp && row.roottemp <= 100">
                            {{ (Math.round(row.roottemp * 10) / 10).toFixed(1) }}
                        </span>
                    </template>
                </el-table-column>
                <el-table-column width="100px" prop="humidity">
                    <template #header>
                        <p>出水温度</p>
                        <p>(℃)</p>
                    </template>
                    <template #default="{ row }">
                        <template v-if="row.humidity && row.humidity <= 100">
                            {{ Math.round(row.humidity) }}
                        </template>
                    </template>
                </el-table-column>
                <el-table-column label="信号强度" width="90px" prop="csq">
                    <template #default="{ row }">
                        <div class="signal-box">
                            <span class="num">{{ row.csq }}</span>
                            <div class="signal">
                                <span
                                    v-for="num of 5"
                                    class="bar"
                                    :key="num"
                                    :class="['bar' + num, num <= Math.floor(row.csq / 6) ? 'active' : '']"
                                ></span>
                            </div>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="上传时间" prop="ts" min-width="160"></el-table-column>
                <el-table-column label="采集时间" prop="data_ts" min-width="160"></el-table-column>
                <el-table-column label="安装位置" min-width="250px" prop="location" show-overflow-tooltip>
                    <template #default="{ row }">
                        <span>{{ row.location.join('，') }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="周期" width="110px" prop="upperi">
                    <template #header>
                        <p>周期</p>
                        <p>(分钟)</p>
                    </template>
                    <template #default="{ row }">{{ row.upperi }}</template>
                </el-table-column>
                <el-table-column label="电池" width="80px" prop="bat">
                    <template #header>
                        <p>电池</p>
                        <p>(%)</p>
                    </template>
                    <template #default="{ row }">
                        <span v-if="row.bat <= 100">{{ row.bat }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="告警标记" width="160px">
                    <template #default="{ row }">
                        <el-tag type="danger" v-if="(row.flags & (1 << 7)) !== 0">掉电故障</el-tag>
                        <el-tag type="danger" v-if="(row.flags & (1 << 6)) !== 0">移位故障</el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="版本号" width="100px" prop="version"></el-table-column>
                <el-table-column label="ICCID" width="200px" prop="iccid"></el-table-column>
                <el-table-column label="首次注册时间" width="160px" prop="reg_ts"></el-table-column>
                <!--                    <el-table-column label="操作" width="160px" fixed="right">
                        <template #default="{ row }">
                            <el-button
                                type="primary"
                                link
                                :icon="View"
                                size="small"
                                v-permission="['pipeline-sensor:history-data:detail']"
                                @click="onShowClick(row)"
                            >
                                详情
                            </el-button>
                        </template>
                    </el-table-column>-->
            </el-table>
            <el-pagination
                class="pagination"
                v-if="table_data.length > 0"
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="page_params.page"
                :page-size="page_params.page_size"
                :page-sizes="[10, 20, 50, 100]"
                layout="total, sizes, prev, pager, next, jumper"
                :total="total"
            ></el-pagination>
        </div>
    </div>
</template>

<script setup>
import { nextTick, onMounted, reactive, ref } from 'vue'
import { Refresh, Search, Upload } from '@element-plus/icons'
import { getDeviceList } from '@/api/heating/devicePoint/devicePoint.js'
import { ElLoading, ElMessage, ElMessageBox } from 'element-plus'
import { getOptionTree } from '@/api/room.js'
import { getEquipModelTree } from '@/api/equipment.js'
import Exceljs from 'exceljs'
import FileSaver from 'file-saver'
import { getDeviceRecord, getDevicesRecord } from '@/api/heating/monitoring.js'

// 型号列表
const model_list = ref({})
async function getModelList() {
    await getEquipModelTree().then((data) => {
        const list = {}
        data.forEach((device_type) => {
            device_type?.children &&
                device_type.children.forEach((device_model) => {
                    list[device_model.id] = {
                        type: 2,
                        model: device_model.name
                    }
                })
        })
        model_list.value = list
    })
}

// 安装位置
const room_list = ref({})
async function getRoomTree() {
    await getOptionTree().then((data) => {
        const room_locations = {}
        function getRoom(list, location) {
            list.forEach((item) => {
                if (item.children) {
                    getRoom(item.children, location + '/' + item.name)
                } else {
                    room_locations[item.id] = location.slice(1) + '/' + item.name
                }
            })
        }
        getRoom(data ? data?.[0]?.children : [], '')
        room_list.value = room_locations
    })
}

// 区域树形数据
const area_tree = ref(null)
const treeData = ref([])
const default_expanded_keys = ref([])

function getTreeData() {
    getOptionTree().then((data) => {
        default_expanded_keys.value = [data[0].id]
        treeData.value = data
        nextTick(() => {
            area_tree.value.setCurrentKey(treeData.value[0].id)
            current_node.value = treeData.value[0]
            getTableData()
        })
    })
}

// 展开层节点时懒加载设备列表
function loadNode(node, resolve) {
    if (node.level === 0) {
        resolve(node.data)
    } else if (node.level <= 5) {
        resolve(node.data.children)
    } else if (node.level === 6) {
        getDeviceList({
            type: 2,
            room: node.data.id
        }).then((data) => {
            const list = data.lists.map((item) => {
                return {
                    ...item,
                    is_leaf: true
                }
            })
            resolve(list)
        })
    } else if (node.level === 7) {
        resolve([])
    }
}

//点击树形节点
const current_node = ref(null)
const handleNodeClick = (node) => {
    current_node.value = node
    search_data.date_range = []
    page_params.page = 1
    total.value = 0
    getTableData()
}

// 最新数据导出
function exportData() {
    ElMessageBox.confirm('确定要导出所选设备的最新数据吗？', '提示', {
        type: 'warning'
    })
        .then(async () => {
            const loading = ElLoading.service({
                lock: true,
                text: '正在导出...',
                spinner: 'Loading',
                background: 'rgba(0, 0, 0, 0.7)'
            })

            let list = []
            await getDevicesRecord({
                type: 2,
                devices: 'all'
            }).then((data) => {
                list = data
            })
            if (list.length <= 0) {
                loading.close()
                return ElMessage.error('没有数据可导出')
            }

            // 创建excel并写入数据
            let workbook = null
            workbook = new Exceljs.Workbook()
            workbook.created = new Date()
            workbook.modified = new Date()
            workbook.lastPrinted = new Date()
            const worksheet = workbook.addWorksheet('设备数据', {
                properties: {
                    defaultRowHeight: 22
                },
                pageSetup: {
                    horizontalCentered: true
                }
            })

            worksheet.columns = [
                {
                    header: '序号',
                    key: 'index',
                    width: 10
                },
                {
                    header: '安装地址',
                    key: 'address',
                    width: 50
                },
                {
                    header: '热力站名称',
                    key: 'station',
                    width: 30
                },
                {
                    header: '小区名称',
                    key: 'residential_name',
                    width: 20
                },
                {
                    header: '楼栋',
                    key: 'building',
                    width: 10
                },
                {
                    header: '单元',
                    key: 'unit',
                    width: 10
                },
                {
                    header: '楼层',
                    key: 'floor',
                    width: 10
                },
                {
                    header: '房间',
                    key: 'room',
                    width: 20
                },
                {
                    header: '户名',
                    key: 'name',
                    width: 20
                },
                {
                    header: '联系电话',
                    key: 'phone',
                    width: 30
                },
                {
                    header: '型号规格',
                    key: 'model',
                    width: 20
                },
                {
                    header: '设备编号（IMEI）',
                    key: 'IMEI',
                    width: 50
                },
                {
                    header: '上传时间',
                    key: 'ts',
                    width: 22
                },
                {
                    header: '采集时间',
                    key: 'data_ts',
                    width: 22
                },
                {
                    header: '入水温度（℃）',
                    key: 'roottemp',
                    width: 15
                },
                {
                    header: '出水温度（℃）',
                    key: 'humidity',
                    width: 15
                },
                {
                    header: '信号强度',
                    key: 'csq',
                    width: 15
                },
                {
                    header: '电池电量（%）',
                    key: 'bat',
                    width: 15
                },
                {
                    header: '告警标记',
                    key: 'alarm_flags',
                    width: 20
                },
                {
                    header: '补偿温度',
                    key: 'temperature_compensation',
                    width: 20
                },
                {
                    header: '上报周期',
                    key: 'upperi',
                    width: 20
                },
                {
                    header: '是否显示',
                    key: 'show_panel',
                    width: 20
                },
                {
                    header: '是否在线',
                    key: 'online',
                    width: 20
                },
                {
                    header: '版本号',
                    key: 'version',
                    width: 20
                },
                {
                    header: 'ICCID',
                    key: 'iccid',
                    width: 20
                }
            ]

            worksheet.addRows(
                list.map((item) => {
                    return {
                        index: item.index,
                        address: item.address,
                        station: item.station,
                        residential_name: item.location[1],
                        building: item.location[2],
                        unit: item.location[3],
                        floor: item.location[4],
                        room: item.location[5],
                        name: item.name,
                        phone: item.phone,
                        model: model_list.value[item.model]
                            ? model_list.value[item.model].type + '/' + model_list.value[item.model].model
                            : '',
                        IMEI: item.IMEI,
                        ts: item.ts,
                        data_ts: item.data_ts,
                        roottemp: item.roottemp,
                        humidity: item.humidity <= 100 ? item.humidity : '',
                        csq: item.csq,
                        bat: item.bat,
                        alarm_flags:
                            ((item.flags & (1 << 7)) !== 0 ? '掉电故障' : '') +
                            ((item.flags & (1 << 7)) !== 0 && (item.flags & (1 << 6)) !== 0 ? '，' : '') +
                            ((item.flags & (1 << 6)) !== 0 ? '移位故障' : ''),
                        temperature_compensation: item.temperature_compensation,
                        upperi: item.upperi,
                        show_panel: item.show_panel ? '显示' : '不显示',
                        online: item.online ? '在线' : '离线',
                        version: item.version,
                        iccid: item.iccid
                    }
                })
            )

            worksheet.getRow(1).alignment = {
                horizontal: 'center',
                vertical: 'middle'
            }

            // 导出excel文件
            await workbook.xlsx.writeBuffer().then((buffer) => {
                const date = new Date()
                const year = date.getFullYear() // 年
                let month = date.getMonth() + 1 // 月
                let day = date.getDate() // 日
                let hour = date.getHours() // 时
                let minutes = date.getMinutes() // 分
                let seconds = date.getSeconds() // 秒
                // 给一位数的数据前面加 “0”
                if (month >= 1 && month <= 9) month = '0' + month
                if (day >= 0 && day <= 9) day = '0' + day
                if (hour >= 0 && hour <= 9) hour = '0' + hour
                if (minutes >= 0 && minutes <= 9) minutes = '0' + minutes
                if (seconds >= 0 && seconds <= 9) seconds = '0' + seconds
                const presentDate = `设备最新监测数据${year}${month}${day}${hour}${minutes}${seconds}`

                // 导出并存储文件
                FileSaver.saveAs(new Blob([buffer]), `${presentDate}.xlsx`)
            })
            loading.close()
        })
        .catch(() => {
            ElMessage.error('导出失败')
        })
}

// 搜索栏
const search_data = reactive({
    date_range: null
})
function resetQueryForm() {
    search_data.date_range = null
    page_params.page = 1
    getTableData()
}

// 表格数据
const table_data = ref([])
const tableLoading = ref(false)
function getTableData() {
    if (!current_node.value.is_leaf) return
    tableLoading.value = true
    const params = {
        ...search_data,
        ...page_params,
        type: 2,
        device_id: current_node.value.id
    }
    if (params.date_range) {
        params.start_time = params.date_range[0]
        params.end_time = params.date_range[1]
    }
    delete params.date_range
    getDeviceRecord(params).then((data) => {
        let device_data = data[0]
        table_data.value = device_data.lists.map((item) => {
            return {
                name: current_node.value.name,
                IMEI: current_node.value.IMEI,
                reg_ts: current_node.value.reg_ts,
                ...item
            }
        })
        total.value = device_data.total
        tableLoading.value = false
    })
}

// 分页
const total = ref(0)
const page_params = reactive({
    page: 1,
    page_size: 10
})
const handleCurrentChange = (currentPage) => {
    page_params.page = currentPage
    getTableData()
}
const handleSizeChange = (currentSize) => {
    page_params.page_size = currentSize
    page_params.page = 1
    getTableData()
}

// 表格多选
const selected_ids = ref([])
function handleSelectionChange(list) {
    selected_ids.value = list.map((item) => {
        return item.id
    })
}

// 切换到前台时候执行
onMounted(() => {
    getTreeData()
    getModelList()
    getRoomTree()
})
</script>

<style scoped>
.area-container {
    width: 320px;
}
</style>
